import { useEffect, useRef, useState } from "react";
import { Zap } from "lucide-react";
import ScoreSubmit from "@/components/landing/games/ScoreSubmit";

const PADS = [
  { id: 0, label: "Kitchen", on: "bg-amber-400 border-amber-500", off: "bg-[#F5EFEB] border-[#E6DCD3]" },
  { id: 1, label: "AC Unit", on: "bg-sky-400 border-sky-500", off: "bg-[#F5EFEB] border-[#E6DCD3]" },
  { id: 2, label: "Garage", on: "bg-emerald-400 border-emerald-500", off: "bg-[#F5EFEB] border-[#E6DCD3]" },
  { id: 3, label: "Upstairs", on: "bg-rose-400 border-rose-500", off: "bg-[#F5EFEB] border-[#E6DCD3]" },
];
const WIN_ROUND = 5;

export default function BreakerMemory() {
  const [seq, setSeq] = useState([]);
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | showing | input | won | lost
  const [lit, setLit] = useState(null);
  const timeouts = useRef([]);

  useEffect(() => () => timeouts.current.forEach(clearTimeout), []);

  const playSeq = (s) => {
    setPhase("showing");
    s.forEach((pad, i) => {
      timeouts.current.push(setTimeout(() => setLit(pad), 600 * i + 400));
      timeouts.current.push(setTimeout(() => setLit(null), 600 * i + 800));
    });
    timeouts.current.push(
      setTimeout(() => {
        setPhase("input");
        setStep(0);
      }, 600 * s.length + 500)
    );
  };

  const start = () => {
    timeouts.current.forEach(clearTimeout);
    const first = [Math.floor(Math.random() * 4)];
    setSeq(first);
    playSeq(first);
  };

  const press = (id) => {
    if (phase !== "input") return;
    setLit(id);
    setTimeout(() => setLit(null), 250);
    if (id !== seq[step]) {
      setPhase("lost");
      return;
    }
    if (step + 1 === seq.length) {
      if (seq.length >= WIN_ROUND) {
        setPhase("won");
        return;
      }
      const next = [...seq, Math.floor(Math.random() * 4)];
      setSeq(next);
      playSeq(next);
    } else {
      setStep(step + 1);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E6DCD3] p-6 sm:p-8 shadow-sm text-center" data-testid="breaker-memory-game">
      <h3 className="font-bold text-base text-[#1E252B]">Breaker Panel Memory</h3>
      <p className="text-xs text-[#736B63] mt-0.5 mb-5">
        Watch the breakers trip, then flip them back in the same order. Reach round {WIN_ROUND}.
      </p>

      <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto">
        {PADS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => press(p.id)}
            data-testid={`breaker-pad-${p.id}`}
            className={`p-4 rounded-xl border-2 transition-colors flex flex-col items-center gap-1 ${
              lit === p.id ? p.on : p.off
            } ${phase === "input" ? "cursor-pointer" : "cursor-default"}`}
          >
            <Zap className={`w-5 h-5 ${lit === p.id ? "text-white" : "text-[#8C847B]"}`} />
            <span className={`text-xs font-bold ${lit === p.id ? "text-white" : "text-[#544D45]"}`}>{p.label}</span>
          </button>
        ))}
      </div>

      <div className="mt-5">
        {phase === "idle" && (
          <button type="button" onClick={start} data-testid="breaker-start-btn" className="px-6 py-2.5 rounded-lg bg-[#D4691E] hover:bg-[#A94E10] text-white text-sm font-bold transition-colors">
            Flip the Main Switch
          </button>
        )}
        {phase === "showing" && <p className="text-xs font-bold text-[#736B63]" data-testid="breaker-status">Watch the panel… round {seq.length}</p>}
        {phase === "input" && <p className="text-xs font-bold text-[#D4691E]" data-testid="breaker-status">Your turn — round {seq.length} of {WIN_ROUND}</p>}
        {phase === "lost" && (
          <div data-testid="breaker-lost">
            <p className="text-sm font-semibold text-[#1E252B] mb-3">Crossed a wire on round {seq.length}. Even pros double-check the panel.</p>
            <button type="button" onClick={start} data-testid="breaker-retry-btn" className="px-6 py-2.5 rounded-lg bg-[#1E252B] hover:bg-[#D4691E] text-white text-sm font-bold transition-colors">
              Try Again
            </button>
            {seq.length > 1 && <ScoreSubmit game="breaker-memory" score={seq.length - 1} scoreLabel="rounds" />}
          </div>
        )}
        {phase === "won" && (
          <div data-testid="breaker-won">
            <p className="text-sm font-semibold text-[#1E252B] mb-3">Panel restored, lights on. I trace real wiring just as carefully.</p>
            <a href="#contact" data-testid="breaker-quote-btn" className="inline-block px-5 py-2 rounded bg-[#D4691E] hover:bg-[#A94E10] text-white text-xs font-bold transition-colors">
              Get a Quote
            </a>
            <ScoreSubmit game="breaker-memory" score={WIN_ROUND} scoreLabel="rounds" />
          </div>
        )}
      </div>
    </div>
  );
}
